/**
 * lawrenceVoice, single source of truth for Mr. Lawrence narration. Routes
 * audio through /api/academy/narrate (ElevenLabs → OpenAI TTS fallback).
 *
 * Hardened against the "spam the speaker" bug:
 *  - Each call supersedes the previous one (generation counter) so only the
 *    LATEST requested line ever plays, no more stacked/overlapping audio.
 *  - The in-flight network request is aborted when a newer call arrives.
 *  - Fetched clips are cached in-memory by text, so "hear it again" is instant
 *    (no 7-15s refetch delay on repeats).
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _currentText = "";
let _gen = 0;            // bumps on every call; stale requests bail out
let _controller = null;  // aborts the in-flight narrate fetch
let _raf = null;         // rAF id for smooth word-highlight progress
const _cache = new Map(); // cacheKey -> base64 mp3

// De-dupe guard: React 18 StrictMode (dev) double-invokes effects, and quiz
// screens re-render, so the SAME narration can be requested more than once.
// We block a re-request while that exact utterance is STILL PLAYING (so autoplay
// can never "read part, then restart and read the whole thing"), and also within
// a short window of the previous request. Replays ({_replay:true}) bypass it.
let _lastEnqueueSig = "";
let _lastEnqueueAt = 0;
let _playingSig = "";     // signature of the utterance currently playing
function _isDuplicateEnqueue(sig, opts) {
  if (opts && opts._replay) return false;
  // Manual taps (Hear it / Hear the choices / per-option speaker) must ALWAYS
  // play — only AUTO-read (autoplay on mount / StrictMode double-invoke) is
  // de-duped, otherwise a stale "still playing" guard makes the buttons no-ops.
  if (!opts || !opts._auto) return false;
  if (sig && sig === _playingSig) return true;        // exact same utterance still live
  const now = Date.now();
  if (sig === _lastEnqueueSig && now - _lastEnqueueAt < 1500) return true;
  _lastEnqueueSig = sig;
  _lastEnqueueAt = now;
  return false;
}

// Autoplay unlock: browsers block programmatic audio.play() until the user has
// interacted with the page. Because a "Hear it" tap fetches TTS first (async
// gap), the play() call can lose its user-gesture context and get blocked. We
// unlock on the FIRST real gesture by playing a silent clip, so every later
// programmatic clip is allowed even after an await.
let _unlocked = false;
function _installAudioUnlock() {
  if (typeof window === "undefined" || _unlocked) return;
  const unlock = () => {
    if (_unlocked) return;
    _unlocked = true;
    try {
      // Unlock the SAME persistent element we play real clips through, so iOS
      // keeps autoplay permission for every later sentence in a lesson/queue.
      const el = _getAudioEl();
      if (el) {
        el.src = "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//OEAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCA";
        el.volume = 0;
        const p = el.play();
        if (p && p.then) p.then(() => { try { el.pause(); el.volume = 1; } catch { /* no-op */ } }).catch(() => {});
      }
    } catch { /* no-op */ }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("touchend", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("touchend", unlock);
  window.addEventListener("keydown", unlock);
}
_installAudioUnlock();

// ---- HTML5 Audio playback engine (Safari-reliable) -------------------------
// Each clip plays through an <audio> element fed a BLOB URL (NOT a data: URL).
// Blob URLs report the correct duration and seek cleanly in Safari, so clips
// always play from the very start to the very end, no clipped first/last word.
// This also avoids the Web Audio "context.resume()" trap that left Safari silent
// on the first taps (Learn-to-Solve) and cut off the first option clips.
let _audio = null;             // active HTMLAudioElement
let _audioEl = null;           // persistent element reused across clips (keeps iOS autoplay unlock)
let _lastFailAt = 0;           // throttles the "narration failed" UI signal
function _getAudioEl() {
  if (_audioEl) return _audioEl;
  const a = typeof Audio !== "undefined" ? new Audio() : null;
  if (a) {
    a.preload = "auto";
    try { a.preservesPitch = true; a.mozPreservesPitch = true; a.webkitPreservesPitch = true; } catch { /* no-op */ }
  }
  _audioEl = a;
  return a;
}
const _urlCache = new Map();   // cacheKey -> object URL

function _b64ToBlobUrl(key, b64) {
  if (_urlCache.has(key)) return _urlCache.get(key);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
  _urlCache.set(key, url);
  return url;
}

// Parent-chosen narration voices. Separate voice/speed per SUBJECT context:
// "reading" (lessons, questions) vs "games" (arcade). The active context is
// set app-wide (by route) so every narration uses the right voice for the
// child that's currently active. Default voice is null = the natural ElevenLabs
// "Mr. Lawrence" voice (NOT an OpenAI tts-1 voice, which sounds robotic).
let _readerPrefs = {
  reading: { voice: null, rate: 1.0 },
  games: { voice: null, rate: 1.0 },
};
let _readerContext = "reading";
function _num(v) { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null; }
export function setReaderContext(ctx) {
  if (ctx === "reading" || ctx === "games") _readerContext = ctx;
}
export function getReaderContext() { return _readerContext; }
export function setReaderPrefs(p = {}) {
  if (p.reading) _readerPrefs.reading = { voice: p.reading.voice || null, rate: _num(p.reading.rate) || 1.0 };
  if (p.games) _readerPrefs.games = { voice: p.games.voice || null, rate: _num(p.games.rate) || 1.0 };
  // Back-compat: a flat { voice, rate } sets the reading context.
  if (p.voice !== undefined || p.rate !== undefined) {
    _readerPrefs.reading = { voice: p.voice || null, rate: _num(p.rate) || 1.0 };
  }
}
export function getReaderPrefs() { return JSON.parse(JSON.stringify(_readerPrefs)); }

// Auto-read ("hands-free") state, resolved app-wide for the active learner: when
// ON, quiz screens read the question + every choice aloud automatically. A parent
// "Kid Voice Lock" (readerLocked) hides the child-facing toggle. Components read
// the current value with getAutoRead() and subscribe to `iw-autoread-changed`.
let _autoRead = (() => { try { return localStorage.getItem("iw_auto_read") === "1"; } catch { return false; } })();
let _readerLocked = (() => { try { return localStorage.getItem("iw_reader_locked") === "1"; } catch { return false; } })();
export function setAutoReadPrefs({ autoRead, readerLocked } = {}) {
  if (autoRead !== undefined) _autoRead = !!autoRead;
  if (readerLocked !== undefined) _readerLocked = !!readerLocked;
  try {
    localStorage.setItem("iw_auto_read", _autoRead ? "1" : "0");
    localStorage.setItem("iw_reader_locked", _readerLocked ? "1" : "0");
  } catch { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent("iw-autoread-changed", {
      detail: { autoRead: _autoRead, readerLocked: _readerLocked },
    }));
  } catch { /* SSR-safe */ }
}
export function getAutoRead() { return _autoRead; }
export function getReaderLocked() { return _readerLocked; }

// Child-facing reading SPEED (applied to every clip via playbackRate; instant,
// pitch-preserved, no re-generation). Persisted PER CHILD so each explorer keeps
// their own chosen speed across sessions and it never resets when switching
// profiles ("Remember Speed"). Falls back to a global value for signed-out use.
const SPEED_KEY = "iw_voice_speed";
function _activeSpeedChild() {
  try {
    const cis = localStorage.getItem("academy_active_child_id");
    if (cis) return cis;
    const l = JSON.parse(localStorage.getItem("isaiah_active_learner") || "null");
    if (l && l.id) return l.id;
  } catch { /* ignore */ }
  return "";
}
function _speedKeyFor(child) { return child ? `${SPEED_KEY}::${child}` : SPEED_KEY; }
function _loadSpeed(child) {
  try {
    let v = parseFloat(localStorage.getItem(_speedKeyFor(child)));
    if (!(v >= 0.5 && v <= 2.0)) v = parseFloat(localStorage.getItem(SPEED_KEY)); // global fallback
    return v >= 0.5 && v <= 2.0 ? v : 1.0;
  } catch { return 1.0; }
}
let _speedChild = _activeSpeedChild();
let _voiceSpeed = _loadSpeed(_speedChild);
// Switch the remembered speed when the active learner changes (parent picks a
// different explorer). Re-reads that child's saved speed and tells every reader.
export function setVoiceChild(childId) {
  const id = childId || "";
  if (id === _speedChild) return;
  _speedChild = id;
  _voiceSpeed = _loadSpeed(id);
  try { window.dispatchEvent(new CustomEvent("iw-voice-speed-changed", { detail: { speed: _voiceSpeed } })); } catch { /* ignore */ }
  if (_audio) { try { _audio.playbackRate = Math.max(0.5, Math.min(2.0, (_audio.__baseRate || 1.0) * _voiceSpeed)); } catch { /* ignore */ } }
}
try { window.addEventListener("active-learner-changed", () => setVoiceChild(_activeSpeedChild())); } catch { /* SSR-safe */ }
export function getVoiceSpeed() { return _voiceSpeed; }
export function setVoiceSpeed(v) {
  _voiceSpeed = Math.max(0.5, Math.min(2.0, Number(v) || 1.0));
  try {
    localStorage.setItem(_speedKeyFor(_speedChild), String(_voiceSpeed));
    localStorage.setItem(SPEED_KEY, String(_voiceSpeed)); // keep global fallback current
  } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("iw-voice-speed-changed", { detail: { speed: _voiceSpeed } })); } catch { /* ignore */ }
  // Apply live to any currently-playing clip.
  if (_audio) { try { _audio.playbackRate = Math.max(0.5, Math.min(2.0, (_audio.__baseRate || 1.0) * _voiceSpeed)); } catch { /* ignore */ } }
}

// Child-facing NARRATOR VOICE picker. Friendly labels map to OpenAI tts-1 voices
// the backend accepts; "" = the site default (Lawrence). Persisted so it sticks.
export const VOICE_OPTIONS = [
  { v: "", label: "Default" },
  { v: "onyx", label: "Deep" },
  { v: "nova", label: "Bright" },
  { v: "fable", label: "Storyteller" },
  { v: "shimmer", label: "Cheerful" },
];
const VOICE_KEY = "iw_voice_pref";
let _voicePref = (() => { try { return localStorage.getItem(VOICE_KEY) || ""; } catch { return ""; } })();
export function getVoicePref() { return _voicePref; }
export function setVoicePref(v) {
  _voicePref = v || "";
  try { localStorage.setItem(VOICE_KEY, _voicePref); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("iw-voice-pref-changed", { detail: { voice: _voicePref } })); } catch { /* ignore */ }
}

// Remember the last thing spoken so a "say that again" button can replay it.
let _lastSpoken = null;         // string (single) OR array (a queue)
let _lastSpokenOpts = {};
export function replayLast() {
  if (!_lastSpoken) return Promise.resolve();
  if (Array.isArray(_lastSpoken)) return playLawrenceQueue(_lastSpoken, { ..._lastSpokenOpts, _replay: true });
  return playLawrence(_lastSpoken, { ..._lastSpokenOpts, _replay: true });
}
export function hasLastSpoken() { return !!_lastSpoken; }

// Pre-warms the backend TTS pipeline (client init + provider connection) once
// per page load, so a child's FIRST "hear it" click responds as fast as later
// ones (was a 2-3s cold start vs ~1s warm). Best-effort, fire-and-forget.
let _warmed = false;
export async function warmLawrence() {
  if (_warmed) return;
  _warmed = true;
  try {
    await axios.post(`${API}/academy/narrate/warmup`, {}, { timeout: 8000 });
  } catch { /* best-effort, a cold first click still works, just slower */ }
}

// Narration event bus, lets on-screen text follow along (word highlighting)
// while a clip plays. Events: { type: "start"|"progress"|"end", text, fraction }.
const _narrationListeners = new Set();
export function onNarration(fn) {
  _narrationListeners.add(fn);
  return () => _narrationListeners.delete(fn);
}
function _emitNarration(evt) {
  _narrationListeners.forEach((fn) => { try { fn(evt); } catch { /* no-op */ } });
}

// djb2 hash over the FULL normalized text, cheap, deterministic, collision-safe.
function _hashStr(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function cacheKeyFor(text, voice, speed) {
  const norm = (text || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  // Hash the FULL text (plus its length), NOT just a prefix. The old key used
  // the first 80 chars, so a truncated prefetch (e.g. tutor answers pre-warmed
  // from text.slice(0,240)) collided with the full message and the tap replayed
  // that SHORT clip, cutting narration off mid-way. v3 busts those bad keys.
  const k = `${_hashStr(norm)}_${norm.length}`;
  return `teacher_v3::${voice || "default"}::${speed || "d"}::${k}`;
}

export function stopLawrence() {
  _gen++; // invalidate any pending/in-flight play so a late clip can NEVER start
  try { if (_controller) _controller.abort(); } catch { /* no-op */ }
  _controller = null;
  try { if (_raf) cancelAnimationFrame(_raf); } catch { /* no-op */ }
  _raf = null;
  _playingSig = "";
  if (_audio) {
    try { _audio.onended = null; _audio.onerror = null; _audio.pause(); _audio.src = ""; } catch { /* no-op */ }
    _audio = null;
  }
  const _ending = _currentText;
  _currentText = "";
  if (_ending) _emitNarration({ type: "end", text: _ending });
  try {
    if (window.__iw_voice_lock === "reading-tutor") delete window.__iw_voice_lock;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  } catch { /* no-op */ }
}

/**
 * Pause the current narration (keeps position). During a queue this simply
 * holds on the current clip; resume continues to the end and the queue advances.
 */
let _pausedAt = 0; // remembers position so resume never restarts the clip
export function pauseLawrence() {
  try {
    if (_audio && !_audio.paused && !_audio.ended) {
      _pausedAt = _audio.currentTime || 0;
      _audio.pause();
      _emitNarration({ type: "pause", text: _currentText });
    }
  } catch { /* no-op */ }
}

/** Resume a paused narration from where it left off. */
export function resumeLawrence() {
  try {
    if (_audio && _audio.paused && !_audio.ended) {
      // Some browsers (Safari) rewind a paused blob clip to 0 on the next
      // play(); restore the saved position so it CONTINUES instead of restarting.
      try { if (_pausedAt > 0.15 && (_audio.currentTime || 0) < 0.1) _audio.currentTime = _pausedAt; } catch { /* no-op */ }
      const p = _audio.play();
      if (p && p.catch) p.catch(() => {});
      _emitNarration({ type: "resume", text: _currentText });
      // Restart the word-highlight progress ticker (it stopped when paused).
      const audio = _audio; const spokenText = _currentText;
      if (_raf) { try { cancelAnimationFrame(_raf); } catch { /* no-op */ } }
      const tick = () => {
        if (_audio !== audio) return;
        if (isFinite(audio.duration) && audio.duration > 0) {
          _emitNarration({ type: "progress", text: spokenText, fraction: Math.min(1, (audio.currentTime + 0.08) / audio.duration) });
        }
        if (!audio.paused && !audio.ended) _raf = requestAnimationFrame(tick);
      };
      _raf = requestAnimationFrame(tick);
    }
  } catch { /* no-op */ }
}

/**
 * Play `text` in Mr. Lawrence's voice. Resolves when playback ends. Pass
 * `{ rate }` to slow it down for kid listeners.
 */
export async function playLawrence(text, opts = {}) {
  if (!text) return;
  const trimmed = text.trim();
  if (!trimmed) return;

  // Same line already playing → ignore the repeat tap (no restart, no stack).
  // Explicit taps ({_replay:true}, e.g. a per-option speaker) bypass this so
  // they can INTERRUPT the "hear all choices" queue and play just that option.
  if (!opts._replay && _currentText === trimmed && _audio) return;
  // Identical request while it's still live / very recent → skip (kills doubles).
  if (_isDuplicateEnqueue("s:" + trimmed, opts)) return;

  if (!opts._replay) { _lastSpoken = trimmed; _lastSpokenOpts = opts; try { window.dispatchEvent(new CustomEvent("iw-last-spoken-changed")); } catch { /* ignore */ } }

  stopLawrence();         // stop current audio + abort previous fetch (bumps _gen)
  const myGen = ++_gen;   // claim the newest generation AFTER the stop
  _playingSig = "s:" + trimmed;
  const p = _playOne(trimmed, opts, myGen);
  p.then(() => { if (myGen === _gen) _playingSig = ""; }).catch(() => {});
  return p;
}

/**
 * Play several lines back-to-back as SEPARATE clips with a natural gap between
 * them. Used for "Hear the choices" so each option is rendered as its own audio
 *, this prevents the TTS from slurring/dropping segments when the values look
 * alike (e.g. 4 / 40 / 400) and gives an even, natural cadence.
 */
export async function playLawrenceQueue(texts, opts = {}) {
  const list = (texts || []).map((t) => String(t || "").trim()).filter(Boolean);
  if (!list.length) return;
  // Identical queue within a short window (StrictMode / rapid re-render) → skip,
  // so autoplay never reads the same question+options twice back-to-back.
  if (_isDuplicateEnqueue("q:" + list.join("|"), opts)) return;
  if (!opts._replay) { _lastSpoken = list.slice(); _lastSpokenOpts = opts; try { window.dispatchEvent(new CustomEvent("iw-last-spoken-changed")); } catch { /* ignore */ } }
  stopLawrence();
  const myGen = ++_gen;
  _playingSig = "q:" + list.join("|");
  const gap = typeof opts.gapMs === "number" ? opts.gapMs : 140;
  // RELIABILITY FIX: play clips with AT MOST ~one TTS request in flight at a
  // time. Firing all option clips in parallel (old Promise.all) overloaded the
  // TTS provider and intermittently dropped a random option ("Option B" silent,
  // etc.). Now: ensure clip 0 is cached, then warm ONLY the next clip while the
  // current one plays. Each fetch retries transient failures, and a clip that
  // ultimately fails is SKIPPED without killing the rest of the queue.
  await _prefetch(list[0], opts);
  if (myGen !== _gen) return;
  for (let i = 0; i < list.length; i++) {
    if (myGen !== _gen) return;
    if (i + 1 < list.length) _prefetch(list[i + 1], opts); // warm next (single in-flight)
    await _playOne(list[i], opts, myGen);  // cached → instant; else fetch w/ retry
    if (myGen !== _gen) return;
    if (i < list.length - 1) await new Promise((r) => setTimeout(r, gap));
  }
  if (myGen === _gen) _playingSig = "";
}

// ---- GAME CALLER (Bingo etc.) — reliability-first, "always speak" ----------
// The quiz narrator (playLawrence) is built to SUPERSEDE: every new request
// aborts the in-flight fetch and stops the current clip, which is correct for a
// quiz ("read THIS instead of that") but wrong for a bingo caller, where each
// short, spaced-out call MUST be vocalized to completion. This dedicated path:
//   • uses its OWN <audio> element, so the quiz narrator never touches it and
//     vice-versa (no cross-abort);
//   • fetches on-demand WITHOUT an abort signal so the request always finishes
//     (and caches for reuse) even while the 40-item prefetch is warming;
//   • ALWAYS falls back to window.speechSynthesis if premium TTS can't produce
//     or play audio — so a called word is NEVER silent.
let _calloutAudio = null;
let _calloutGen = 0;

function _browserSpeak(text) {
  try {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text || ""));
    u.rate = 0.95; u.pitch = 1.0; u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch { /* no-op */ }
}

export function stopCallout() {
  _calloutGen++;
  if (_calloutAudio) {
    try { _calloutAudio.onended = null; _calloutAudio.onerror = null; _calloutAudio.pause(); _calloutAudio.src = ""; } catch { /* no-op */ }
    _calloutAudio = null;
  }
  try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch { /* no-op */ }
}

export async function speakCallout(text, opts = {}) {
  const trimmed = String(text == null ? "" : text).trim();
  if (!trimmed) return;
  const myGen = ++_calloutGen;
  // Stop only the PREVIOUS callout (not the quiz narrator).
  if (_calloutAudio) {
    try { _calloutAudio.onended = null; _calloutAudio.onerror = null; _calloutAudio.pause(); _calloutAudio.src = ""; } catch { /* no-op */ }
    _calloutAudio = null;
  }
  try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch { /* no-op */ }

  const voice = opts.voice || _voicePref || _readerPrefs[opts.context || _readerContext]?.voice || null;
  const speed = (opts.speed && opts.speed > 0) ? Math.max(0.25, Math.min(4.0, opts.speed)) : null;
  const key = cacheKeyFor(trimmed, voice, speed);

  const playB64 = (b64) => new Promise((resolve) => {
    if (myGen !== _calloutGen) return resolve(false);
    let url;
    try { url = _b64ToBlobUrl(key, b64); } catch { return resolve(false); }
    const audio = new Audio();
    audio.preload = "auto";
    audio.src = url;
    try { audio.load(); } catch { /* no-op */ }
    _calloutAudio = audio;
    let settled = false;
    const done = (ok) => { if (settled) return; settled = true; resolve(ok); };
    audio.onended = () => { if (_calloutAudio === audio) _calloutAudio = null; done(true); };
    audio.onerror = () => done(false);
    // Safety net: if playback never fires end/error, resolve so the fallback can run.
    setTimeout(() => done(false), 12000);
    const p = audio.play();
    if (p && p.catch) p.catch(() => done(false));
  });

  // 1) already cached (from prefetch or a prior call) → premium voice, instant.
  const cachedB64 = _cache.get(key);
  if (cachedB64) {
    const ok = await playB64(cachedB64);
    if (myGen !== _calloutGen) return;
    if (!ok) _browserSpeak(trimmed);
    return;
  }

  // 2) NOT cached → 2-second guarantee. Kick off the premium fetch, but do NOT
  // let a cold-start (ElevenLabs first request can take 10-14s) leave the child
  // waiting in silence. Race the fetch against a ~1.8s timer: if the clip is
  // ready in time we play the premium voice; otherwise we IMMEDIATELY speak the
  // word with the device voice so it's heard within ~2s, never silent, never
  // delayed. The premium fetch keeps running in the background and caches for
  // next time (so a replay/next round is instant + premium).
  const FALLBACK_MS = 1800;
  const fetchP = _narrate(trimmed, key, voice, speed, { retries: 2 })
    .then((b64) => { if (b64) _cache.set(key, b64); return b64; })
    .catch(() => null);
  const timerP = new Promise((res) => setTimeout(() => res("__timeout__"), FALLBACK_MS));
  const winner = await Promise.race([fetchP, timerP]);
  if (myGen !== _calloutGen) return; // a newer callout superseded this one

  if (winner && winner !== "__timeout__") {
    // Premium clip arrived within the budget → play it.
    const ok = await playB64(winner);
    if (myGen !== _calloutGen) return;
    if (!ok) _browserSpeak(trimmed);
    return;
  }
  // Budget exceeded (or fetch failed) → speak NOW with the device voice so the
  // child hears the word immediately. The premium clip finishes caching quietly.
  _browserSpeak(trimmed);
}

const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

// GLOBAL multiple-choice voice standard. The verified-correct phrasing is baked
// in as the default; setOptionPhrasing() can override it from the admin-managed
// config, but only if the new template keeps BOTH {letter} and {text} anchors -
// so a bad admin value can NEVER break option audio (it falls back to this).
let _optionTemplate = "Option {letter} is {text}.";
let _optionEmptyTemplate = "Option {letter}.";
export function setOptionPhrasing(template, emptyTemplate) {
  if (typeof template === "string" && template.includes("{letter}") && template.includes("{text}")) {
    _optionTemplate = template;
  }
  if (typeof emptyTemplate === "string" && emptyTemplate.includes("{letter}")) {
    _optionEmptyTemplate = emptyTemplate;
  }
}
export function getOptionPhrasing() { return { template: _optionTemplate, emptyTemplate: _optionEmptyTemplate }; }

// Spoken phrasing for ONE multiple-choice option. VERIFIED via Whisper STT:
// "Option A. Asia." made tts-1 MERGE the letter into the word or DROP the word;
// the full sentence "Option A is Asia." reads every letter + word/number
// reliably. Each option is ALWAYS played as its OWN clip (never combined).
export function optionLine(index, text) {
  const letter = OPTION_LETTERS[index] || String(index + 1);
  let t = String(text == null ? "" : text).trim()
    .replace(/&/g, " and ")
    .replace(/\s{2,}/g, " ")
    .replace(/[.\s]+$/, "")   // drop any trailing period/space; the template supplies one
    .trim();
  return t
    ? _optionTemplate.replace("{letter}", letter).replace("{text}", t)
    : _optionEmptyTemplate.replace("{letter}", letter);
}

// Combined single-clip phrasing (kept for any caller that still wants one clip).
export function optionsSpeech(list) {
  return (list || []).map((o, i) => optionLine(i, o)).filter(Boolean).join(" ");
}

// The full narration line-up for a question: the prompt, then EACH option as its
// own line/clip. Used by autoplay, warm-next prefetch and topic prewarm so they
// all cache/play the exact same clips.
export function narrationLinesFor(question, options = []) {
  const list = (options || []).filter((o) => o != null && String(o).trim() !== "");
  return [String(question || "").trim(), ...list.map((o, i) => optionLine(i, o))].filter(Boolean);
}

// Speak one option (per-choice speaker), padded, reliable single clip. Uses
// _replay so tapping a choice ALWAYS interrupts the "hear all choices" queue.
export function playOption(index, text, opts = {}) {
  return playLawrence(optionLine(index, text), { _replay: true, ...opts });
}

// Speak all options back-to-back, each as its OWN clip with a short gap, so no
// option is ever dropped and there's a clear pause between A / B / C / D.
export function playOptionsQueue(list, opts = {}) {
  const lines = (list || []).map((o, i) => optionLine(i, o)).filter(Boolean);
  return lines.length ? playLawrenceQueue(lines, { gapMs: 220, ...opts }) : Promise.resolve();
}

// Fetch narrated audio for one clip with RETRIES. The backend caches by
// cache_key (DB + disk), so a retry of an already-generated clip is cheap and
// burns no extra credits, retries only re-hit the provider for transient
// failures (ElevenLabs 429/5xx/timeouts), which were the real cause of the
// "one option never reads" bug. Returns base64 mp3, or throws after retries.
async function _narrate(trimmed, key, voice, speed, { signal, retries = 2 } = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const r = await axios.post(
        `${API}/academy/narrate`,
        { text: trimmed, cache_key: key, ...(voice ? { voice } : {}), ...(speed ? { speed } : {}) },
        { timeout: 20000, ...(signal ? { signal } : {}) }
      );
      const b64 = r.data?.audio_base64;
      if (b64) return b64;
      lastErr = new Error("empty audio");
    } catch (e) {
      // A deliberate supersede/abort, propagate so callers bail quietly.
      if ((axios.isCancel && axios.isCancel(e)) || e?.name === "CanceledError" || e?.name === "AbortError") throw e;
      lastErr = e;
    }
    if (attempt < retries) await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
  }
  throw lastErr || new Error("narrate failed");
}

// Fetch + decode a clip WITHOUT playing it, so a later play is instant. Decoding
// up front (not just fetching) means playback starts with zero decode delay.
async function _prefetch(text, opts = {}) {
  const trimmed = String(text == null ? "" : text).trim();
  if (!trimmed) return;
  const voice = opts.forceDefaultVoice ? null : (opts.voice || _voicePref || _readerPrefs[opts.context || _readerContext]?.voice || null);
  const speed = (opts.speed && opts.speed > 0) ? Math.max(0.25, Math.min(4.0, opts.speed)) : null;
  const key = cacheKeyFor(trimmed, voice, speed);
  if (_urlCache.has(key)) return;
  let b64 = _cache.get(key);
  if (!b64) {
    try {
      b64 = await _narrate(trimmed, key, voice, speed, { retries: 2 });
      if (b64) _cache.set(key, b64);
    } catch { return; }
  }
  if (b64) { try { _b64ToBlobUrl(key, b64); } catch { /* best-effort */ } }
}
export function prefetchLawrence(texts, opts = {}) {
  (Array.isArray(texts) ? texts : [texts]).forEach((t) => _prefetch(t, opts));
}

// Sequential prefetch, warms clips ONE AT A TIME so a batch of LONG clips (e.g.
// the 5 Learn-to-Solve explanations) can't flood the backend and leave a tapped
// clip stuck for 15-25s behind them. The first clip is ready first.
export async function prefetchLawrenceSequential(texts, opts = {}) {
  const list = Array.isArray(texts) ? texts : [texts];
  for (const t of list) {
    // eslint-disable-next-line no-await-in-loop
    await _prefetch(t, opts);
  }
}

// Parallel prewarm with a small concurrency cap — warms a whole batch (e.g. an
// entire Bingo bank) in a few seconds so a randomly-called item is already
// cached and speaks INSTANTLY, without flooding the TTS provider all at once.
export async function prefetchLawrenceParallel(texts, opts = {}, concurrency = 6) {
  const list = (Array.isArray(texts) ? texts : [texts]).filter(Boolean);
  let idx = 0;
  const worker = async () => {
    while (idx < list.length) {
      const my = idx++;
      // eslint-disable-next-line no-await-in-loop
      await _prefetch(list[my], opts);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, list.length || 1) }, worker));
}

// How many of the given callout lines already have audio cached and decoded
// (ready to play instantly). Powers the Bingo "voices ready" meter so the caller
// only starts once the bank is warmed. Same key derivation as playback.
export function countCalloutsReady(texts, opts = {}) {
  const voice = opts.voice || _voicePref || _readerPrefs[opts.context || _readerContext]?.voice || null;
  const speed = (opts.speed && opts.speed > 0) ? Math.max(0.25, Math.min(4.0, opts.speed)) : null;
  let n = 0;
  (Array.isArray(texts) ? texts : [texts]).forEach((t) => {
    const trimmed = String(t == null ? "" : t).trim();
    if (!trimmed) return;
    if (_cache.has(cacheKeyFor(trimmed, voice, speed))) n++;
  });
  return n;
}

// Core single-clip player. Assumes the caller already bumped `_gen` to `myGen`
// and called stopLawrence(). Resolves when this clip finishes (or is superseded).
async function _playOne(trimmed, opts, myGen) {
  _currentText = trimmed;
  window.__iw_voice_lock = "reading-tutor";
  const voice = opts.forceDefaultVoice ? null : (opts.voice || _voicePref || _readerPrefs[opts.context || _readerContext]?.voice || null);
  const rate = opts.rate || _readerPrefs[opts.context || _readerContext]?.rate || 1.0;
  const speed = (opts.speed && opts.speed > 0) ? Math.max(0.25, Math.min(4.0, opts.speed)) : null;
  const key = cacheKeyFor(trimmed, voice, speed);

  const startPlayback = async (b64) => {
    if (myGen !== _gen) return;
    const url = _b64ToBlobUrl(key, b64);
    // REUSE one persistent <audio> element for every clip. iOS Safari grants
    // autoplay per-element after a gesture; a fresh `new Audio()` per sentence
    // LOST that grant, so clips 2+ were silently blocked and the lesson
    // "stopped" partway. One unlocked element = reads start-to-finish.
    const audio = _getAudioEl();
    if (!audio) return;
    try { audio.onended = null; audio.onerror = null; audio.pause(); } catch { /* no-op */ }
    audio.__baseRate = rate || 1.0;
    audio.volume = 1;
    audio.playbackRate = Math.max(0.5, Math.min(2.0, (rate || 1.0) * _voiceSpeed));
    audio.src = url;
    try { audio.load(); } catch { /* no-op */ }
    _audio = audio;
    const spokenText = trimmed;
    // Wait until the clip has BUFFERED ENOUGH to play from the very start
    // (readyState 3+/canplaythrough), not just metadata. Starting at merely
    // HAVE_METADATA let the decoder skip the first fraction of a second on the
    // 2nd+ clip of a queue, dropping the leading word (e.g. the "Option B."
    // prefix). Blob URLs are fully in memory so this resolves near-instantly.
    await new Promise((res) => {
      let done = false;
      const go = () => { if (!done) { done = true; res(); } };
      if (audio.readyState >= 3) return go();
      audio.addEventListener("canplaythrough", go, { once: true });
      audio.addEventListener("canplay", go, { once: true });
      setTimeout(go, 1500);
    });
    if (myGen !== _gen || _audio !== audio) return;
    try { audio.currentTime = 0; } catch { /* no-op */ }
    const tick = () => {
      if (myGen !== _gen || _audio !== audio) return;
      if (isFinite(audio.duration) && audio.duration > 0) {
        _emitNarration({ type: "progress", text: spokenText, fraction: Math.min(1, (audio.currentTime + 0.08) / audio.duration) });
      }
      if (!audio.paused && !audio.ended) _raf = requestAnimationFrame(tick);
    };
    return new Promise((resolve) => {
      audio.onended = () => {
        if (_audio === audio) { _currentText = ""; }
        _emitNarration({ type: "end", text: spokenText });
        resolve();
      };
      audio.onerror = () => resolve();
      audio.play().then(() => {
        _emitNarration({ type: "start", text: spokenText });
        _raf = requestAnimationFrame(tick);
      }).catch(() => resolve());
    });
  };

  // Instant replay from cache, no network, no chance of stacking.
  const cached = _cache.get(key);
  if (cached) return startPlayback(cached);

  try {
    _controller = new AbortController();
    const b64 = await _narrate(trimmed, key, voice, speed, { signal: _controller.signal, retries: 2 });
    if (myGen !== _gen) return; // superseded while fetching
    if (!b64) throw new Error("no audio");
    _cache.set(key, b64);
    return startPlayback(b64);
  } catch (e) {
    // A superseding call aborted this fetch, that's expected, stay quiet.
    if ((axios.isCancel && axios.isCancel(e)) || e?.name === "CanceledError" || e?.name === "AbortError") return;
    // POLICY: never fall back to window.speechSynthesis (wrong voice).
    // IMPORTANT: do NOT stopLawrence() here, inside a queue that would cancel
    // every remaining clip. Just skip THIS one; the queue loop moves on.
    // Signal a genuine, final narration failure so the UI can nudge the child
    // to tap the speaker to try again (throttled — never spams).
    try {
      const now = Date.now();
      if (now - _lastFailAt > 4000) {
        _lastFailAt = now;
        window.dispatchEvent(new CustomEvent("iw-narration-failed", { detail: { text: trimmed } }));
      }
    } catch { /* SSR-safe */ }
    // eslint-disable-next-line no-console
    console.warn("[lawrenceVoice] narrate failed after retries; skipping clip:", trimmed);
  }
}
